import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useEmployees,
  useAttendance,
  useShifts,
  useLeave,
  usePayrollPolicies,
} from '../../services/httpServices/queries';
import type { Attendance, Leave, Shift } from '../../services/httpServices/hrService';
import { ShiftManagement } from './roster/ShiftManagement';
import { AssignShiftModal, type AssignModalData } from './roster/AssignShiftModal';
import { BulkAssignModal } from './roster/BulkAssignModal';
import { DailyRosterPrintModal } from './roster/DailyRosterPrintModal';
import {
  addDays,
  extractTime,
  findShift,
  formatISODate,
  formatShiftTime,
  getWeekStart,
  isDateInRange,
  isSameDay,
  isWeeklyOffDay,
  resolveWeeklyOffDays,
  shiftColorStyle,
} from './roster/utils';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  Layers,
  CalendarPlus,
  Clock,
  User,
  Umbrella,
  Moon,
  Printer,
} from 'lucide-react';

type Tab = 'roster' | 'shifts';

export function RosterManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [activeTab, setActiveTab] = useState<Tab>(() =>
    params.get('tab') === 'shifts' ? 'shifts' : 'roster'
  );
  const today = new Date();
  const [weekStart, setWeekStart] = useState(() => getWeekStart(today));
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<AssignModalData | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showDailyRoster, setShowDailyRoster] = useState(false);

  // Sync tab to URL
  useEffect(() => {
    const newParams = new URLSearchParams(location.search);
    if (activeTab === 'shifts') newParams.set('tab', 'shifts');
    else newParams.delete('tab');
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const weekEnd = addDays(weekStart, 6);
  const dateFrom = formatISODate(weekStart);
  const dateTo = formatISODate(weekEnd);

  // Prefer today when it is inside the visible week, otherwise open on the
  // first day of the week the user is looking at.
  const todayKey = formatISODate(today);
  const defaultDailyDate = isDateInRange(todayKey, dateFrom, dateTo) ? todayKey : dateFrom;

  const { data: employeesData, isLoading: employeesLoading } = useEmployees(1, 500, { status: 'active' });
  const { data: attendanceData, isLoading: attendanceLoading } = useAttendance(undefined, dateFrom, dateTo);
  const { data: leaveData, isLoading: leaveLoading } = useLeave(undefined, 'APPROVED', dateFrom, dateTo);
  const { data: shiftsData, isLoading: shiftsLoading } = useShifts();
  const { data: payrollPoliciesData, isLoading: payrollPoliciesLoading } = usePayrollPolicies();

  const employees = useMemo(() => employeesData?.data || [], [employeesData]);
  const shifts = useMemo(() => shiftsData?.data || [], [shiftsData]);
  const payrollPolicies = useMemo(
    () => payrollPoliciesData?.data || [],
    [payrollPoliciesData],
  );

  const weeklyOffDaysByEmployee = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const employee of employees as any[]) {
      map.set(employee.id, resolveWeeklyOffDays(employee, payrollPolicies));
    }
    return map;
  }, [employees, payrollPolicies]);

  const days = useMemo(() => {
    const arr: Date[] = [];
    for (let i = 0; i < 7; i++) arr.push(addDays(weekStart, i));
    return arr;
  }, [weekStart]);

  const attendanceMap = useMemo(() => {
    const map = new Map<string, Attendance>();
    for (const record of attendanceData?.data || []) {
      const dateKey =
        typeof record.date === 'string' ? record.date.slice(0, 10) : formatISODate(new Date(record.date));
      const key = `${record.employeeId}-${dateKey}`;
      map.set(key, record);
    }
    return map;
  }, [attendanceData]);

  const leaveMap = useMemo(() => {
    const map = new Map<string, Leave>();
    for (const leave of leaveData?.data || []) {
      for (const day of days) {
        const dateStr = formatISODate(day);
        if (isDateInRange(dateStr, leave.startDate, leave.endDate)) {
          const key = `${leave.employeeId}-${dateStr}`;
          map.set(key, leave);
        }
      }
    }
    return map;
  }, [leaveData, days]);

  const openAssignModal = (employeeId: string, employeeName: string, date: string) => {
    const key = `${employeeId}-${date}`;
    setModalData({
      employeeId,
      employeeName,
      date,
      existing: attendanceMap.get(key),
      onLeave: leaveMap.get(key),
      isWeeklyOff: isWeeklyOffDay(date, weeklyOffDaysByEmployee.get(employeeId) || []),
    });
    setShowModal(true);
  };

  const prevWeek = () => setWeekStart((prev) => addDays(prev, -7));
  const nextWeek = () => setWeekStart((prev) => addDays(prev, 7));
  const currentWeek = () => setWeekStart(getWeekStart(today));

  const resolveShift = (record?: Attendance): Shift | undefined => {
    if (!record) return undefined;
    if (record.shift) return record.shift;
    const byId = findShift(shifts, record.shiftId);
    if (byId) return byId;
    return shifts.find((s) => s.shiftCode === record.shiftType || s.name === record.shiftType);
  };

  const isLoading =
    employeesLoading ||
    attendanceLoading ||
    leaveLoading ||
    shiftsLoading ||
    payrollPoliciesLoading;

  const tabs = [
    { key: 'roster' as Tab, label: 'Roster', icon: CalendarDays },
    { key: 'shifts' as Tab, label: 'Shifts', icon: Layers },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalendarDays className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-text">Duty Roster</h1>
            <p className="text-sm text-text-muted">Build weekly rosters and manage the shifts you assign.</p>
          </div>
        </div>
        {activeTab === 'roster' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDailyRoster(true)}
              title="Download or print the daily duty roster for the notice board"
              className="px-3 py-2 text-sm font-medium text-text border border-border rounded-lg hover:bg-background transition-colors flex items-center gap-2"
            >
              <Printer size={16} />
              Daily Roster
            </button>
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-3 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2"
            >
              <CalendarPlus size={16} />
              Bulk Assign
            </button>
            <button
              onClick={currentWeek}
              className="px-3 py-2 text-sm font-medium text-text-muted hover:text-text border border-border rounded-lg hover:bg-background transition-colors"
            >
              Today
            </button>
            <button
              onClick={prevWeek}
              className="p-2 border border-border rounded-lg hover:bg-background transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-medium text-text min-w-[180px] text-center">
              {weekStart.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })} -{' '}
              {weekEnd.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </span>
            <button
              onClick={nextWeek}
              className="p-2 border border-border rounded-lg hover:bg-background transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex bg-surface border border-border rounded-lg overflow-hidden w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-primary text-white'
                : 'text-text-muted hover:text-text hover:bg-background'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'shifts' && <ShiftManagement />}

      {activeTab === 'roster' && (
        <>
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {!isLoading && employees.length === 0 && (
            <div className="flex items-center gap-2 p-4 bg-warning/10 border border-warning/20 rounded-lg text-warning">
              <AlertCircle size={18} />
              <span>No active employees found. Add employees to build the roster.</span>
            </div>
          )}

          {!isLoading && employees.length > 0 && shifts.length === 0 && (
            <div className="flex items-center gap-2 p-4 bg-warning/10 border border-warning/20 rounded-lg text-warning">
              <AlertCircle size={18} />
              <span>
                No shifts configured yet. Create shifts in the <strong>Shifts</strong> tab before assigning.
              </span>
            </div>
          )}

          {!isLoading && employees.length > 0 && (
            <div className="bg-surface rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-background/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider sticky left-0 bg-background/50 min-w-[200px] z-10">
                        Employee
                      </th>
                      {days.map((day) => {
                        const isToday = isSameDay(day, today);
                        return (
                          <th
                            key={day.toISOString()}
                            className={`px-3 py-3 text-center min-w-[130px] ${isToday ? 'bg-primary/5' : ''}`}
                          >
                            <div className="text-xs font-medium text-text-muted uppercase tracking-wider">
                              {day.toLocaleDateString('en-US', { weekday: 'short' })}
                            </div>
                            <div className={`text-sm font-semibold mt-0.5 ${isToday ? 'text-primary' : 'text-text'}`}>
                              {day.getDate()}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {employees.map((employee: any) => (
                      <tr key={employee.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-3 sticky left-0 bg-surface min-w-[200px] z-10">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <User size={14} className="text-primary" />
                            </div>
                            <div>
                              <button
                                onClick={() => navigate(`/employees/${employee.id}`)}
                                className="text-sm font-medium text-text hover:text-primary transition-colors text-left"
                              >
                                {employee?.firstName} {employee?.lastName}
                              </button>
                              <p className="text-xs text-text-muted">{employee.department?.name || 'N/A'}</p>
                            </div>
                          </div>
                        </td>
                        {days.map((day) => {
                          const dateStr = formatISODate(day);
                          const key = `${employee.id}-${dateStr}`;
                          const record = attendanceMap.get(key);
                          const onLeave = leaveMap.get(key);
                          const shift = resolveShift(record);
                          const employeeOffDays = weeklyOffDaysByEmployee.get(employee.id) || [];
                          const isWeeklyOff = isWeeklyOffDay(day, employeeOffDays);
                          const isOffDay = isWeeklyOff && !record;
                          const startTime = extractTime(record?.checkIn) || formatShiftTime(shift?.startTime);
                          const endTime = extractTime(record?.checkOut) || formatShiftTime(shift?.endTime);
                          const isToday = isSameDay(day, today);

                          return (
                            <td key={dateStr} className={`px-3 py-2 text-center ${isToday ? 'bg-primary/5' : ''}`}>
                              {onLeave ? (
                                <div
                                  className="inline-flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-xs font-medium bg-danger/10 text-danger w-full cursor-default"
                                  title={`On ${onLeave.type} Leave${onLeave.reason ? `: ${onLeave.reason}` : ''}`}
                                >
                                  <span className="flex items-center gap-1">
                                    <Umbrella size={12} />
                                    On Leave
                                  </span>
                                </div>
                              ) : isOffDay ? (
                                <button
                                  onClick={() =>
                                    openAssignModal(
                                      employee.id,
                                      `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
                                      dateStr
                                    )
                                  }
                                  title="Weekly off day. Click to assign an overtime shift."
                                  className="inline-flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors hover:opacity-80 w-full border bg-amber-500/10 text-amber-700 border-amber-500/25"
                                >
                                  <span className="flex items-center gap-1">
                                    <Moon size={12} />
                                    Off Day
                                  </span>
                                </button>
                              ) : (
                                <button
                                  onClick={() =>
                                    openAssignModal(
                                      employee.id,
                                      `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
                                      dateStr
                                    )
                                  }
                                  className={`inline-flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors hover:opacity-80 w-full border ${
                                    shift
                                      ? ''
                                      : 'bg-background text-text-muted border-border'
                                  }`}
                                  style={shift ? shiftColorStyle(shift.color) : undefined}
                                >
                                  <span className="flex items-center gap-1">
                                    <Clock size={12} />
                                    {shift ? shift.name : record?.shiftType || 'Assign'}
                                  </span>
                                  {startTime && endTime && (
                                    <span className="text-[0.625rem] opacity-80">
                                      {startTime} - {endTime}
                                    </span>
                                  )}
                                </button>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <AssignShiftModal
        open={showModal}
        data={modalData}
        shifts={shifts.filter((s) => s.isActive || s.id === modalData?.existing?.shiftId)}
        onClose={() => {
          setShowModal(false);
          setModalData(null);
        }}
      />

      <BulkAssignModal
        open={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        employees={employees}
        shifts={shifts}
      />

      {showDailyRoster && (
        <DailyRosterPrintModal
          onClose={() => setShowDailyRoster(false)}
          initialDate={defaultDailyDate}
        />
      )}
    </div>
  );
}
